import * as React from 'react';
import { CloseLine } from '@mingcute/react';

import { cn } from '../lib/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  clearable?: boolean;
  onClear?: () => void;
  invalid?: boolean;
  inputSize?: 'sm' | 'md' | 'lg';
}

const sizeMap = {
  sm: 'h-8 text-xs',
  md: 'h-9 text-sm',
  lg: 'h-11 text-base',
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = 'text',
      leftIcon,
      rightIcon,
      clearable,
      onClear,
      invalid,
      inputSize = 'md',
      disabled,
      value,
      ...props
    },
    ref,
  ) => {
    const showClear = clearable && !!value && !disabled;
    return (
      <div
        className={cn(
          'group flex items-center gap-2 rounded-[var(--tvl-r2)] border bg-[var(--tvl-raised)] px-3',
          'transition-colors',
          // Focus = claret border + a claret ring that hugs it (no offset → not a double).
          // NB: use ring-[var(...)] (sets --tw-ring-color) and border-[var(...)], NOT
          // outline-[var(...)] — Tailwind parses outline-[var(...)] as a WIDTH, so the
          // colour fell back to currentColor (near-white ink) and rendered a white ring.
          'focus-within:ring-2 focus-within:ring-[var(--tvl-focus)]',
          invalid
            ? 'border-[var(--tvl-danger)]'
            : 'border-[var(--tvl-border-input)] focus-within:border-[var(--tvl-focus)]',
          disabled && 'cursor-not-allowed opacity-60',
          sizeMap[inputSize],
          className,
        )}
      >
        {leftIcon ? (
          <span className="shrink-0 text-[var(--tvl-ink-3)]">{leftIcon}</span>
        ) : null}
        <input
          ref={ref}
          type={type}
          value={value}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={cn(
            'h-full min-w-0 flex-1 bg-transparent text-[var(--tvl-ink)] placeholder:text-[var(--tvl-ink-3)]',
            'outline-none disabled:cursor-not-allowed',
          )}
          {...props}
        />
        {showClear ? (
          <button
            type="button"
            aria-label="Clear"
            onClick={onClear}
            className="shrink-0 rounded-full p-0.5 text-[var(--tvl-ink-3)] hover:text-[var(--tvl-ink)]"
          >
            <CloseLine size={14} />
          </button>
        ) : rightIcon ? (
          <span className="shrink-0 text-[var(--tvl-ink-3)]">{rightIcon}</span>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
